export interface SonioxContextGeneralHint {
    key: string,
    value: string,
}

export interface SonioxContext {
    general?: SonioxContextGeneralHint[],
    terms?: string[],
    [key: string]: unknown,
}

export const SONIOX_CONTEXT_MAX_LENGTH = 10000;

const isJsonObject = (value: unknown): value is Record<string, unknown> => {
    return value != null && typeof value === 'object' && !Array.isArray(value);
};

export const isSonioxProvider = (asrProvider?: string): boolean => {
    return asrProvider?.toLowerCase() === 'soniox';
};

export const trimSonioxContextJson = (value?: string): string => {
    return (value ?? '').trim();
};

export const parseSonioxContext = (value?: string): SonioxContext | null => {
    const trimmedValue = trimSonioxContextJson(value);

    if (!trimmedValue.length) {
        return null;
    }

    const parsedValue = JSON.parse(trimmedValue);

    if (!isJsonObject(parsedValue)) {
        return null;
    }

    return parsedValue as SonioxContext;
};

export const sanitizeSonioxGeneralHints = (generalHints?: SonioxContextGeneralHint[]): SonioxContextGeneralHint[] => {
    if (!generalHints?.length) {
        return [];
    }

    return generalHints
        .map((generalHint) => ({
            key: generalHint?.key?.trim() ?? '',
            value: generalHint?.value?.trim() ?? '',
        }))
        .filter((generalHint) => !!generalHint.key.length && !!generalHint.value.length);
};

export const sanitizeSonioxTerms = (terms?: string[]): string[] => {
    if (!terms?.length) {
        return [];
    }

    const uniqueTerms = new Set<string>();

    terms.forEach((term) => {
        const trimmedTerm = term?.trim() ?? '';

        if (!!trimmedTerm.length) {
            uniqueTerms.add(trimmedTerm);
        }
    });

    return Array.from(uniqueTerms);
};

export const getSonioxGeneralHints = (context?: SonioxContext | null): SonioxContextGeneralHint[] => {
    if (!context || !Array.isArray(context.general)) {
        return [];
    }

    return context.general
        .filter((generalHint) => isJsonObject(generalHint))
        .map((generalHint) => ({
            key: typeof generalHint.key === 'string' ? generalHint.key : '',
            value: typeof generalHint.value === 'string' ? generalHint.value : '',
        }));
};

export const getSonioxTerms = (context?: SonioxContext | null): string[] => {
    if (!context || !Array.isArray(context.terms)) {
        return [];
    }

    return context.terms.filter((term): term is string => typeof term === 'string');
};

export const getSonioxExtraFields = (context?: SonioxContext | null): Record<string, unknown> => {
    const extraFields = {} as Record<string, unknown>;

    if (!context) {
        return extraFields;
    }

    Object.keys(context).forEach((key) => {
        if (key !== 'general' && key !== 'terms') {
            extraFields[key] = context[key];
        }
    });

    return extraFields;
};

export const buildSonioxContext = (extraFields: Record<string, unknown>, generalHints: SonioxContextGeneralHint[], terms: string[]): SonioxContext | null => {
    const nextContext = { ...extraFields } as SonioxContext;
    const sanitizedGeneralHints = sanitizeSonioxGeneralHints(generalHints);
    const sanitizedTerms = sanitizeSonioxTerms(terms);

    if (sanitizedGeneralHints.length) {
        nextContext.general = sanitizedGeneralHints;
    }

    if (sanitizedTerms.length) {
        nextContext.terms = sanitizedTerms;
    }

    return Object.keys(nextContext).length ? nextContext : null;
};

export const serializeSonioxContext = (context?: SonioxContext | null): string => {
    if (!context) {
        return '';
    }

    return JSON.stringify(context);
};

export const normalizeSonioxContextJson = (value?: string): string => {
    const trimmedValue = trimSonioxContextJson(value);

    if (!trimmedValue.length) {
        return '';
    }

    try {
        const parsedContext = parseSonioxContext(trimmedValue);

        if (!parsedContext) {
            return trimmedValue;
        }

        return serializeSonioxContext(parsedContext);
    } catch {
        return trimmedValue;
    }
};

export const formatSonioxContextJson = (value?: string): string => {
    const trimmedValue = trimSonioxContextJson(value);

    if (!trimmedValue.length) {
        return '';
    }

    try {
        const parsedContext = parseSonioxContext(trimmedValue);

        if (!parsedContext) {
            return trimmedValue;
        }

        return JSON.stringify(parsedContext, null, 2);
    } catch {
        return trimmedValue;
    }
};

export const getSonioxContextSerializedLength = (value?: string): number => {
    const normalizedValue = normalizeSonioxContextJson(value);

    return normalizedValue.length;
};

export const getSonioxContextValidationError = (value?: string, maxLength = SONIOX_CONTEXT_MAX_LENGTH): string => {
    const trimmedValue = trimSonioxContextJson(value);

    if (!trimmedValue.length) {
        return '';
    }

    try {
        const parsedValue = JSON.parse(trimmedValue);

        if (!isJsonObject(parsedValue)) {
            return 'Soniox context must be a JSON object.';
        }

        if (JSON.stringify(parsedValue).length > maxLength) {
            return `Soniox context must be ${maxLength} characters or less.`;
        }

        return '';
    } catch {
        return 'Soniox context must be valid JSON.';
    }
};
